
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout/app-layout';
import { Loader2 } from 'lucide-react';

// Lazy load pages for dynamic code-splitting and smaller initial bundles
const DashboardPage = React.lazy(() => import('@/pages/dashboard-page').then(m => ({ default: m.DashboardPage })));
const TicketsPage = React.lazy(() => import('@/pages/tickets-page').then(m => ({ default: m.TicketsPage })));
const TicketDetailPage = React.lazy(() => import('@/pages/ticket-detail-page').then(m => ({ default: m.TicketDetailPage })));
const CreateTicketPage = React.lazy(() => import('@/pages/create-ticket-page').then(m => ({ default: m.CreateTicketPage })));
const ImportPage = React.lazy(() => import('@/pages/import-page').then(m => ({ default: m.ImportPage })));
const SettingsPage = React.lazy(() => import('@/pages/settings-page').then(m => ({ default: m.SettingsPage })));
const AccountingPage = React.lazy(() => import('@/pages/accounting-page').then(m => ({ default: m.AccountingPage })));
const InventoryPage = React.lazy(() => import('@/pages/inventory-page').then(m => ({ default: m.InventoryPage })));
const ApprovalsPage = React.lazy(() => import('@/pages/approvals-page').then(m => ({ default: m.ApprovalsPage })));
const PerformancePage = React.lazy(() => import('@/pages/performance-page').then(m => ({ default: m.PerformancePage })));
const InvoicesPage = React.lazy(() => import('@/pages/invoices-page').then(m => ({ default: m.InvoicesPage })));
const StockNotificationsPage = React.lazy(() => import('@/pages/stock-notifications-page').then(m => ({ default: m.StockNotificationsPage })));
const ProductTrackingPage = React.lazy(() => import('@/pages/product-tracking-page').then(m => ({ default: m.ProductTrackingPage })));
const PosPage = React.lazy(() => import('@/pages/pos-page').then(m => ({ default: m.PosPage })));
const ContactsPage = React.lazy(() => import('@/pages/contacts-page').then(m => ({ default: m.ContactsPage })));
const TrainingPage = React.lazy(() => import('@/pages/training-page').then(m => ({ default: m.TrainingPage })));
const SalesPage = React.lazy(() => import('@/pages/sales-page').then(m => ({ default: m.SalesPage })));
const CrmPage = React.lazy(() => import('@/pages/crm-page').then(m => ({ default: m.CrmPage })));
const PurchasesPage = React.lazy(() => import('@/pages/purchases-page').then(m => ({ default: m.PurchasesPage })));
const ProductsPage = React.lazy(() => import('@/pages/products-page').then(m => ({ default: m.ProductsPage })));
const EmployeesPage = React.lazy(() => import('@/pages/employees-page').then(m => ({ default: m.EmployeesPage })));
const FieldServicePage = React.lazy(() => import('@/pages/field-service-page').then(m => ({ default: m.FieldServicePage })));
const ManufacturingPage = React.lazy(() => import('@/pages/manufacturing-page').then(m => ({ default: m.ManufacturingPage })));
const SparePartsPage = React.lazy(() => import('@/pages/spare-parts-page').then(m => ({ default: m.SparePartsPage })));
const WarehousePage = React.lazy(() => import('@/pages/warehouse-page').then(m => ({ default: m.WarehousePage })));
const PayrollPage = React.lazy(() => import('@/pages/payroll-page').then(m => ({ default: m.PayrollPage })));
const AttendancePage = React.lazy(() => import('@/pages/attendance-page').then(m => ({ default: m.AttendancePage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthGuard>
          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-center space-y-4">
                  <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground">جاري التحميل...</p>
                </div>
              </div>
            }
          >
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/tickets/new" element={<CreateTicketPage />} />
                <Route path="/tickets/:id" element={<TicketDetailPage />} />
                <Route path="/import" element={<ImportPage />} />
                <Route path="/accounting" element={<AccountingPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/performance" element={<PerformancePage />} />
                <Route path="/invoices" element={<InvoicesPage />} />
                <Route path="/stock-notifications" element={<StockNotificationsPage />} />
                <Route path="/product-tracking" element={<ProductTrackingPage />} />
                <Route path="/pos" element={<PosPage />} />
                <Route path="/training" element={<TrainingPage />} />
                <Route path="/field-service" element={<FieldServicePage />} />
                <Route path="/manufacturing" element={<ManufacturingPage />} />
                <Route path="/spare-parts" element={<SparePartsPage />} />
                <Route path="/warehouse" element={<WarehousePage />} />
                <Route path="/payroll" element={<PayrollPage />} />
                <Route path="/attendance" element={<AttendancePage />} />
                <Route path="/sales" element={<SalesPage />} />
                <Route path="/crm" element={<CrmPage />} />
                <Route path="/purchases" element={<PurchasesPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthGuard>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

